import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { createUserWithEmailAndPassword, updateProfile, signOut } from 'firebase/auth';
import { functionsInstance, auth } from '../../../services/firebase/firebase-config';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import '../../../styles/account-completion-form.css';

const AccountCompletionForm = ({ 
  verifiedEmail,
  linkedinUrl = '',
  onSubmissionSuccess, 
  onError, 
  onGoBack 
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    referredBy: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-focus on mount for accessibility
  useEffect(() => {
    const timer = setTimeout(() => {
      const firstInput = document.querySelector('#firstName');
      if (firstInput) {
        firstInput.focus();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const validateForm = () => {
    const errors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }



    return errors;
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Step 1: Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, verifiedEmail, formData.password);
      const user = userCredential.user;
      
      // Step 2: Update the user's display name
      await updateProfile(user, {
        displayName: `${formData.firstName.trim()} ${formData.lastName.trim()}`
      });
      
      // Step 3: Create Firestore profile
      const completeAccountCreation = httpsCallable(functionsInstance, 'completeAccountCreation');
      const profileData = {
        userId: user.uid,
        email: verifiedEmail,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        linkedinUrl: linkedinUrl.trim(),
        referredBy: formData.referredBy.trim()
      };
      
      console.log('Creating profile with data:', profileData);
      const result = await completeAccountCreation(profileData);
      
      if (result.data.success) {
        // Success! The user now has a real Firebase Auth account and Firestore profile
        // Sign them out immediately - waitlist users should not be authenticated
        // They'll see a success confirmation on the landing page
        await signOut(auth);
        
        onSubmissionSuccess({
          userId: user.uid,
          email: verifiedEmail,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          linkedinUrl: linkedinUrl.trim(),
          status: 'waitlist'
        });
      } else {
        // If Firestore profile creation failed, clean up the Auth account
        await user.delete();
        onError(result.data.message || 'Failed to create profile');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      
      let errorMessage = 'Failed to create account. Please try again.';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists. Please try logging in instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'functions/failed-precondition') {
        errorMessage = 'Email verification expired. Please start over.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Verification session not found. Please start over.';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = error.message || 'Please check your information and try again.';
      }
      
      onError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="account-completion-form-container">
      {/* Header Card */}
      <div className="bg-white dark:bg-gray-800 mb-4 p-8 rounded-lg">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Last Step!</h2>
          <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed max-w-md mx-auto">
            Your email is verified. Just a few more details.
          </p>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg">

        <form onSubmit={handleSubmit} className="account-completion-form space-y-6">

        {/* Name Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="firstName" className="block text-sm font-semibold text-gray-900 dark:text-white">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className={`w-full px-4 py-3.5 border rounded-lg transition-all duration-200 text-base font-medium ${
                formErrors.firstName 
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 focus:border-red-500 focus:ring-2 focus:ring-red-100 dark:focus:ring-red-900/50 text-gray-900 dark:text-white' 
                  : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 dark:focus:ring-purple-900/50 text-gray-900 dark:text-white'
              }`}
              placeholder="Enter your first name"
              disabled={isSubmitting}
              autoComplete="given-name"
            />
            {formErrors.firstName && (
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{formErrors.firstName}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="lastName" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className={`w-full px-4 py-3.5 border rounded-lg transition-all duration-200 text-base font-medium ${
                formErrors.lastName 
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 focus:border-red-500 focus:ring-2 focus:ring-red-100 dark:focus:ring-red-900/50 text-gray-900 dark:text-white' 
                  : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 dark:focus:ring-purple-900/50 text-gray-900 dark:text-white'
              }`}
              placeholder="Enter your last name"
              disabled={isSubmitting}
              autoComplete="family-name"
            />
            {formErrors.lastName && (
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{formErrors.lastName}</p>
            )}
          </div>
        </div>

        {/* Password Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Password *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full px-4 py-3.5 border rounded-lg transition-all duration-200 text-base font-medium ${
                formErrors.password 
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 focus:border-red-500 focus:ring-2 focus:ring-red-100 dark:focus:ring-red-900/50 text-gray-900 dark:text-white' 
                  : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 dark:focus:ring-purple-900/50 text-gray-900 dark:text-white'
              }`}
              placeholder="Create password"
              disabled={isSubmitting}
            />
            {formErrors.password && (
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{formErrors.password}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-900 dark:text-white">
              Confirm Password *
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`w-full px-4 py-3.5 border rounded-lg transition-all duration-200 text-base font-medium ${
                formErrors.confirmPassword 
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 focus:border-red-500 focus:ring-2 focus:ring-red-100 dark:focus:ring-red-900/50 text-gray-900 dark:text-white' 
                  : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 dark:focus:ring-purple-900/50 text-gray-900 dark:text-white'
              }`}
              placeholder="Confirm password"
              disabled={isSubmitting}
            />
            {formErrors.confirmPassword && (
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{formErrors.confirmPassword}</p>
            )}
          </div>
        </div>

        {/* Password Requirements */}
        <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
          <p className="text-sm text-gray-700 dark:text-gray-300 font-medium flex items-center gap-2">
            <svg className="h-4 w-4 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
            Must be at least 8 characters with uppercase, lowercase, and number
          </p>
        </div>


        {/* Referral Source */}
        <div className="space-y-2">
          <label htmlFor="referredBy" className="block text-sm font-semibold text-gray-900 dark:text-white">
            Referred By (Optional)
          </label>
          <input
            type="text"
            id="referredBy"
            name="referredBy"
            value={formData.referredBy}
            onChange={handleInputChange}
            className="w-full px-4 py-3.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 dark:focus:ring-purple-900/50 transition-all duration-200 text-base font-medium text-gray-900 dark:text-white"
            placeholder="Enter referrer name or source"
            disabled={isSubmitting}
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200 text-lg ${
              isSubmitting
                ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-900/50'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-3">
                <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Joining Waitlist...
              </div>
            ) : (
              'Join Waitlist'
            )}
          </button>
        </div>
      </form>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
            By joining the waitlist, you agree to our{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountCompletionForm;